/**
 * Utility functions for date handling with IST (Indian Standard Time) support
 */

/**
 * Get the current date in IST (Indian Standard Time) as YYYY-MM-DD format
 * @returns {string} Current date in IST as YYYY-MM-DD
 */
export const getTodayInIST = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istNow = new Date(utc + istOffset);
  return istNow.toISOString().split("T")[0];
};

/**
 * Get the current date and time in IST as a readable string
 * @returns {string} Current date and time in IST
 */
export const getCurrentISTDateTime = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const istNow = new Date(utc + istOffset);
  return istNow.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/**
 * Check if an appointment date matches today in IST
 * @param {string} appointmentDate - The appointment date to check
 * @returns {boolean} True if the appointment is for today in IST
 */
export const isAppointmentToday = (appointmentDate) => {
  if (!appointmentDate) return false;

  const today = getTodayInIST();

  // Normalize appointment date format
  let normalizedAppointmentDate;
  if (appointmentDate.includes("T")) {
    normalizedAppointmentDate = appointmentDate.split("T")[0];
  } else {
    normalizedAppointmentDate = appointmentDate;
  }

  // Debug logging
  console.log(
    `🔍 isAppointmentToday: ${normalizedAppointmentDate} === ${today} ? ${
      normalizedAppointmentDate === today
    }`
  );

  return normalizedAppointmentDate === today;
};

/**
 * Format a date string for display
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export const formatAppointmentDate = (dateString) => {
  try {
    if (!dateString) return "N/A";

    // Handle date strings in YYYY-MM-DD format to avoid timezone issues
    if (
      typeof dateString === "string" &&
      dateString.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      // Parse as local date to avoid timezone conversion
      const [year, month, day] = dateString.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } else {
      // Handle other date formats
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  } catch {
    return dateString || "N/A";
  }
};

/**
 * Get today's date in a readable format
 * @returns {string} Today's date in readable format
 */
export const getTodayString = () => {
  const now = new Date();
  return now.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};
